/* bf-core.js — BF mobile redesign (2026-09) · integrator-owned · CONTRACTS.md §5–§9.
   Exposes window.BF: media gate, event bus, scroll lock, sheets/backdrop/focus, Section Rendering, cart API.
   Deferred (after constants.js / pubsub.js / global.js); every behaviour gates on BF.isMobile(). Streams call it, never edit it. */
(function () {
  'use strict';
  if (window.BF && window.BF.__core) return;
  var doc = document;
  var MQ = '(max-width: 749px)';
  var mql = window.matchMedia(MQ);
  var rmql = window.matchMedia('(prefers-reduced-motion: reduce)');
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  var openStack = [];   // sheet ids in the order they were opened; last = top
  var openers = {};     // id → element to refocus on close
  var locks = [];       // ref-counted scroll lock owners
  var savedScrollY = 0;

  function listen(m, fn) { if (m.addEventListener) m.addEventListener('change', fn); else m.addListener(fn); return function () { if (m.removeEventListener) m.removeEventListener('change', fn); else m.removeListener(fn); }; }
  function backdrop() { return doc.getElementById('bf-backdrop'); }
  function backdropShow() { var b = backdrop(); if (!b) return; if (typeof bindBackdrop === 'function') bindBackdrop(); b.hidden = false; void b.offsetHeight; b.classList.add('is-open'); }
  function backdropHide() { var b = backdrop(); if (!b) return; b.classList.remove('is-open'); b.hidden = true; }
  function needsBackdrop() { for (var i = 0; i < openStack.length; i++) if (openStack[i] !== 'search') return true; return false; }
  function focusables(root) { return Array.prototype.slice.call(root.querySelectorAll(FOCUSABLE)).filter(function (n) { return n.offsetWidth || n.offsetHeight || n.getClientRects().length; }); }

  var BF = {
    __core: true,
    version: '2026-09-07',
    mq: MQ,
    isMobile: function () { return mql.matches; },
    onMobileChange: function (fn) { return listen(mql, fn); },
    delay: function (ms) { return new Promise(function (r) { setTimeout(r, BF.reduced ? 0 : ms); }); },
    ready: function (fn) { if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', fn); else fn(); },

    /* ---- events (§5): CustomEvent on document, bubbles:false ---- */
    emit: function (name, detail) { doc.dispatchEvent(new CustomEvent(name, { detail: detail || {}, bubbles: false })); },
    on: function (name, fn) { doc.addEventListener(name, fn); return function () { doc.removeEventListener(name, fn); }; },

    /* ---- small DOM helpers ---- */
    qs: function (sel, root) { return (root || doc).querySelector(sel); },
    qsa: function (sel, root) { return Array.prototype.slice.call((root || doc).querySelectorAll(sel)); },
    parseHTML: function (html) { return new DOMParser().parseFromString(html, 'text/html'); },

    /* ---- scroll lock (§9): ref-counted, iOS-safe via html.bf-locked ---- */
    lock: function (id) {
      id = id || 'anon';
      if (!locks.length) { savedScrollY = window.scrollY || window.pageYOffset || 0; doc.documentElement.classList.add('bf-locked'); }
      if (locks.indexOf(id) === -1) locks.push(id);
    },
    unlock: function (id) {
      id = id || 'anon'; var i = locks.indexOf(id); if (i !== -1) locks.splice(i, 1);
      if (!locks.length) { doc.documentElement.classList.remove('bf-locked'); if (Math.abs((window.scrollY || 0) - savedScrollY) > 1) window.scrollTo(0, savedScrollY); }
    },
    locked: function () { return locks.length > 0; },

    /* ---- sheets (§9): [data-bf-sheet="<id>"] roots; ids menu | search | sort | size | actions | bag ---- */
    sheetEl: function (id) { return doc.querySelector('[data-bf-sheet="' + id + '"]'); },
    currentSheet: function () { return openStack.length ? openStack[openStack.length - 1] : null; },
    isOpen: function (id) { return openStack.indexOf(id) !== -1; },
    openSheet: function (id, opts) {
      opts = opts || {};
      if (!BF.isMobile()) return false;
      var el = BF.sheetEl(id); if (!el) { if (window.console) console.warn('[BF] no sheet registered for', id); return false; }
      if (BF.isOpen(id)) return true;
      BF.lock(id);
      openStack.slice().forEach(function (o) { if (o !== id) BF.closeSheet(o, { restoreFocus: false }); });
      openers[id] = opts.opener || doc.activeElement;
      el.hidden = false; void el.offsetHeight;
      el.classList.add('is-open'); el.setAttribute('aria-hidden', 'false');
      if (id !== 'search') backdropShow();
      openStack.push(id);
      if (opts.focus !== false) {
        var target = el.querySelector('[data-bf-autofocus]') || focusables(el)[0] || el;
        if (target === el && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        requestAnimationFrame(function () { try { target.focus({ preventScroll: true }); } catch (e) { /* noop */ } });
      }
      BF.emit('bf:sheet:open', { id: id });
      if (id === 'bag') BF.emit('bf:drawer:open', { id: 'bag' });
      return true;
    },
    closeSheet: function (id, opts) {
      opts = opts || {};
      var el = BF.sheetEl(id); var idx = openStack.indexOf(id);
      if (!el || idx === -1) return false;
      openStack.splice(idx, 1);
      el.classList.remove('is-open', 'is-dragging', 'is-settling'); el.style.transform = '';
      el.hidden = true; el.setAttribute('aria-hidden', 'true');
      if (!needsBackdrop()) backdropHide();
      BF.unlock(id);
      var op = openers[id]; delete openers[id];
      if (opts.restoreFocus !== false && op && op.focus && doc.contains(op)) { try { op.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
      BF.emit('bf:sheet:close', { id: id });
      if (id === 'bag') BF.emit('bf:drawer:close', { id: 'bag' });
      return true;
    },
    closeAll: function () { openStack.slice().reverse().forEach(function (id) { BF.closeSheet(id); }); },
    toggleSheet: function (id, opts) { return BF.isOpen(id) ? BF.closeSheet(id) : BF.openSheet(id, opts); },

    /* ---- Section Rendering API (§7) ---- */
    renderSection: function (url, sectionId, opts) {
      opts = opts || {};
      var u = url + (url.indexOf('?') !== -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId);
      return fetch(u, { credentials: 'same-origin', headers: { 'Accept': 'text/html' } }).then(function (r) {
        if (!r.ok) throw new Error('renderSection ' + r.status + ' ' + u);
        return r.text();
      }).then(function (html) {
        var d = BF.parseHTML(html);
        var root = d.getElementById('shopify-section-' + sectionId) || d.body;
        if (opts.replace) {
          var live = doc.getElementById('shopify-section-' + sectionId);
          if (live) { live.innerHTML = root.innerHTML; BF.emit('bf:section:rendered', { sectionId: sectionId, url: u, root: live }); return live; }
        }
        return root;
      });
    },
    sectionIdFromDom: function () { var el = doc.querySelector('main .shopify-section[id^="shopify-section-template--"]'); return el ? el.id.replace('shopify-section-', '') : null; }
  };

  /* ---- cart API (§8) ---- */
  function routes() { return window.routes || {}; }
  function cartPost(path, body) {
    return fetch(path + '.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) })
      .then(function (r) { return r.text().then(function (t) { var j = null; try { j = t ? JSON.parse(t) : null; } catch (e) { /* noop */ } if (!r.ok) { var err = new Error((j && (j.description || j.message)) || ('Cart error ' + r.status)); err.status = r.status; err.body = j; throw err; } return j; }); });
  }
  function sectionsParam(sections) { return sections && sections.length ? (Array.isArray(sections) ? sections.join(',') : String(sections)) : undefined; }
  function announce(json, sections, source) {
    var cartPromise = (json && typeof json.item_count === 'number') ? Promise.resolve(json) : BF.cart.get();
    return cartPromise.then(function (cart) {
      var detail = { item_count: cart.item_count, sections: (json && json.sections) || null, cart: cart, source: source || 'sync' };
      BF.emit('bf:cart:updated', detail);
      return detail;
    });
  }
  BF.cart = {
    get: function () { return fetch((routes().cart_url || '/cart') + '.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } }).then(function (r) { if (!r.ok) throw new Error('cart.js ' + r.status); return r.json(); }); },
    add: function (item, sections, source) {
      var body = { items: [item] }; var s = sectionsParam(sections); if (s) body.sections = s;
      return cartPost(routes().cart_add_url || '/cart/add', body).then(function (j) { return announce(j, sections, source || 'atc'); });
    },
    change: function (payload, sections, source) {
      var body = {}; for (var k in payload) if (Object.prototype.hasOwnProperty.call(payload, k)) body[k] = payload[k]; var s = sectionsParam(sections); if (s) body.sections = s;
      return cartPost(routes().cart_change_url || '/cart/change', body).then(function (j) { return announce(j, sections, source || 'drawer'); });
    },
    update: function (payload, sections, source) {
      var body = {}; for (var k in payload) if (Object.prototype.hasOwnProperty.call(payload, k)) body[k] = payload[k]; var s = sectionsParam(sections); if (s) body.sections = s;
      return cartPost(routes().cart_update_url || '/cart/update', body).then(function (j) { return announce(j, sections, source || 'cart-page'); });
    },
    sync: function (source) { return BF.cart.get().then(function (c) { return announce(c, null, source || 'sync'); }); }
  };

  /* ---- declarative hooks: [data-bf-open="<id>"], [data-bf-close], backdrop click ---- */
  doc.addEventListener('click', function (e) {
    var t = e.target; if (!t || !t.closest) return;
    var b = backdrop(); if (b && t === b) { BF.closeAll(); return; }
    var closer = t.closest('[data-bf-close]');
    if (closer) { var sheet = closer.closest('[data-bf-sheet]'); if (sheet) { e.preventDefault(); BF.closeSheet(sheet.getAttribute('data-bf-sheet')); } else BF.closeAll(); return; }
    var opener = t.closest('[data-bf-open]');
    if (opener && BF.isMobile()) { e.preventDefault(); BF.openSheet(opener.getAttribute('data-bf-open'), { opener: opener }); }
  });

  /* ---- keyboard: Escape closes the top sheet; Tab is trapped inside it ---- */
  doc.addEventListener('keydown', function (e) {
    var id = BF.currentSheet(); if (!id) return;
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); BF.closeSheet(id); return; }
    if (e.key !== 'Tab') return;
    var el = BF.sheetEl(id); var f = focusables(el); if (!f.length) { e.preventDefault(); return; }
    var first = f[0], last = f[f.length - 1], inside = el.contains(doc.activeElement);
    if (e.shiftKey && (doc.activeElement === first || !inside)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (doc.activeElement === last || !inside)) { e.preventDefault(); first.focus(); }
  });

  /* ---- ORR1 A-3 · drag-to-dismiss, and the backdrop tap -------------------------------------
     Replaces the grip-only handler that dismissed on a fixed `dy > 90` with no velocity term.
     Applies to the bottom sheets menu | bag | sort | size | actions. `search` is EXCLUDED — it is
     full-screen with no backdrop (CONTRACTS §9) and keeps exactly today's behaviour.
     openSheet / closeSheet semantics, the focus trap, lock / unlock and every event name are
     untouched (CONTRACTS §5, §14); this block only decides when closeSheet is called.

       drag surface : the grip, plus the top 25 % of the sheet's OWN height, full width. A control
                      (link, button, field) in that band outside the scrolling body never starts a
                      drag, so [data-bf-close] and the close button still close on a tap.
       body swipe   : anywhere inside .bf-sheet__body while it is at scrollTop <= 0. Once it has
                      been scrolled down a downward swipe scrolls and does not drag; and if a body
                      drag's finger reverses above its start (or the body leaves the top), the drag
                      is cancelled and the gesture goes back to the scroller.
       dismissal    : a downward flick of >= 0.5 px/ms measured over the last <= 120 ms, at ANY
                      travel, OR a travel of >= 30 % of the sheet's own height. Anything else
                      springs back to 0 through the existing .is-settling transition.

     No touch default is ever prevented here, so a finger that does not move still activates the
     control under it. The `touch-action` declarations live in assets/bf-chrome.css (stream A). */
  var DRAG_SHEETS = { menu: 1, bag: 1, sort: 1, size: 1, actions: 1 };
  var DRAG_FLICK_V = 0.5;    /* px/ms, downward */
  var DRAG_FLICK_MS = 120;   /* the window that velocity is measured over */
  var DRAG_TRAVEL = 0.30;    /* fraction of the sheet's own height that a slow drag must cover */
  var DRAG_HEAD = 0.25;      /* fraction of the sheet's own height that is the head band */
  var DRAG_SLOP = 10;        /* px of movement below which a touch is still a tap */
  var DRAG_SKIP = 'a[href],button,input,select,textarea,label,[role="button"],[contenteditable="true"]';
  var drag = null;

  function dragVelocity(d) {
    var p = d.pts, last = p[p.length - 1], first = last;
    for (var i = p.length - 1; i >= 0; i--) { if (last.t - p[i].t <= DRAG_FLICK_MS) first = p[i]; else break; }
    var dt = last.t - first.t;
    return dt > 0 ? (last.y - first.y) / dt : 0;
  }
  function dragStop(close) {
    if (!drag) return;
    var d = drag; drag = null; var s = d.s;
    s.classList.remove('is-dragging');
    s.style.transform = '';
    if (close) { BF.closeSheet(d.id); return; }
    if (!d.moved) return;                       /* it was a tap: nothing to settle */
    s.classList.add('is-settling');
    setTimeout(function () { s.classList.remove('is-settling'); }, BF.reduced ? 0 : 260);
  }
  doc.addEventListener('touchstart', function (e) {
    if (drag || !e.touches || e.touches.length !== 1) return;
    var t = e.target; if (!t || !t.closest) return;
    var s = t.closest('[data-bf-sheet]'); if (!s) return;
    var id = s.getAttribute('data-bf-sheet');
    if (!DRAG_SHEETS[id] || !s.classList.contains('is-open')) return;
    var r = s.getBoundingClientRect(); if (!r.height) return;
    var y = e.touches[0].clientY;
    var onGrip = !!t.closest('[data-bf-grip], .bf-sheet__grip');
    var body = s.querySelector('.bf-sheet__body');
    var inBody = !!(body && body.contains(t));
    var inHead = y <= r.top + r.height * DRAG_HEAD;
    if (!onGrip) {
      if (!inBody && t.closest(DRAG_SKIP)) return;   /* a control in the chrome: the tap owns it */
      if (inBody && body.scrollTop > 0) return;      /* scrolled body: it scrolls, it does not drag */
      if (!inBody && !inHead) return;                /* chrome below the head band is not a surface */
    }
    drag = { s: s, id: id, h: r.height, y0: y, dy: 0, moved: false,
             body: inBody ? body : null, fromBody: inBody && !onGrip, pts: [{ y: y, t: Date.now() }] };
  }, { passive: true });
  doc.addEventListener('touchmove', function (e) {
    if (!drag || !e.touches || !e.touches.length) return;
    var y = e.touches[0].clientY, dy = y - drag.y0;
    if (drag.fromBody && (dy < 0 || drag.body.scrollTop > 0)) { dragStop(false); return; }
    drag.pts.push({ y: y, t: Date.now() }); if (drag.pts.length > 16) drag.pts.shift();
    if (dy <= 0) { drag.dy = 0; if (drag.moved) drag.s.style.transform = 'translateY(0px)'; return; }
    drag.dy = dy;
    if (!drag.moved) { drag.moved = true; drag.s.classList.add('is-dragging'); }
    drag.s.style.transform = 'translateY(' + dy + 'px)';
  }, { passive: true });
  doc.addEventListener('touchend', function () {
    if (!drag) return;
    if (!drag.moved) { dragStop(false); return; }
    dragStop(dragVelocity(drag) >= DRAG_FLICK_V || drag.dy >= drag.h * DRAG_TRAVEL);
  }, { passive: true });
  doc.addEventListener('touchcancel', function () { dragStop(false); }, { passive: true });

  /* ---- ORR1 A-3 · backdrop tap. MEASURED, not assumed (verify/A-orr1/self/A3-backdrop-cause.json):
     a real WebKit tap on #bf-backdrop produces NO click event at all, so the delegated document
     click listener above never runs and the sheet stays open. Three controlled trials on the live
     preview: baseline -> stays open; `cursor: pointer` alone -> still stays open; a click listener
     bound DIRECTLY TO THE ELEMENT -> closes. WebKit synthesises the click only when the element is
     itself a click target. So the backdrop gets its own listeners; the delegated path above is
     left exactly as it was, and still serves mouse input. touchend is non-passive purely so the
     dismissal can preventDefault and stop a ghost click landing on whatever the sheet covered. */
  function bindBackdrop() {
    var b = backdrop(); if (!b || b.__bfTap) return; b.__bfTap = true;
    var x0 = null, y0 = null;
    b.addEventListener('touchstart', function (e) { var t = e.touches[0]; x0 = t.clientX; y0 = t.clientY; }, { passive: true });
    b.addEventListener('touchend', function (e) {
      var t = e.changedTouches && e.changedTouches[0], moved = false;
      if (t && y0 !== null) moved = Math.abs(t.clientY - y0) > DRAG_SLOP || Math.abs(t.clientX - x0) > DRAG_SLOP;
      x0 = y0 = null;
      if (moved || !openStack.length) return;
      e.preventDefault();
      BF.closeAll();
    }, { passive: false });
    b.addEventListener('click', function () { if (openStack.length) BF.closeAll(); });
  }
  BF.ready(bindBackdrop);

  /* ---- leaving mobile closes everything (desktop keeps its own behaviour) ---- */
  listen(mql, function () { if (!BF.isMobile()) BF.closeAll(); });

  /* ---- cart sync: once on load from the server-rendered count; on bfcache restore re-read /cart.js ---- */
  function bootCartSync() {
    var el = doc.querySelector('[data-bf-cart-count]'); if (!el) return;
    var n = parseInt(el.getAttribute('data-bf-cart-count'), 10);
    if (!isNaN(n)) BF.emit('bf:cart:updated', { item_count: n, sections: null, cart: null, source: 'load' });
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted && BF.isMobile()) BF.cart.sync('sync').catch(function () { /* noop */ }); });

  /* ---- I1 (2.11, integrator 2026-09-07): prefetch + cross-document view-transition pairing (mobile only) ----
     touchstart on a product card (C0 emits data-bf-vt="product-<id>"): prefetch its URL (<link rel=prefetch> everywhere; a
     speculationrules script where the browser supports it) and give the tapped card's image the view-transition-name
     product-<id>; on the PDP the first gallery image takes the same name, so a supporting browser (Safari 18.2+, Chrome 126+)
     morphs card -> gallery across the navigation. Every other browser just navigates. One named element per name per page. */
  var prefetched = {};
  function prefetch(href) {
    if (!href || prefetched[href]) return; prefetched[href] = 1;
    try {
      var l = doc.createElement('link'); l.rel = 'prefetch'; l.href = href; l.as = 'document'; doc.head.appendChild(l);
      if (window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {
        var s = doc.createElement('script'); s.type = 'speculationrules';
        s.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls: [href], eagerness: 'immediate' }] });
        doc.head.appendChild(s);
      }
    } catch (e) { /* noop */ }
  }
  function handleFrom(href) { var m = String(href || '').match(/\/products\/([a-z0-9-]+)/i); return m ? m[1].toLowerCase() : ''; }
  var namedCardImg = null;
  function nameCard(a) {
    var img = a.querySelector('img'), h = handleFrom(a.getAttribute('href')); if (!img || !h) return;
    if (namedCardImg && namedCardImg !== img) namedCardImg.style.viewTransitionName = '';
    img.style.viewTransitionName = 'product-' + h; namedCardImg = img;
  }
  /* both card kinds: C0's snippet (.bf-card) and the theme's legacy inline collection cards (no data-bf-vt) — the name is handle-based so the PDP can derive it from its own URL */
  function cardFrom(e) { return e.target && e.target.closest ? e.target.closest('a.bf-card[href], a.collection-product-card[href]') : null; }
  doc.addEventListener('touchstart', function (e) { if (!BF.isMobile()) return; var a = cardFrom(e); if (!a) return; prefetch(a.href); nameCard(a); }, { passive: true });
  doc.addEventListener('click', function (e) { if (!BF.isMobile()) return; var a = cardFrom(e); if (a) nameCard(a); }, true);
  BF.ready(function () {
    if (!BF.isMobile()) return;
    var h = handleFrom(location.pathname), first = doc.querySelector('.bf-gallery__slide img');
    if (h && first) first.style.viewTransitionName = 'product-' + h;
  });

  Object.defineProperty(BF, 'reduced', { get: function () { return rmql.matches; } });
  window.BF = BF;
  BF.ready(function () { bootCartSync(); BF.emit('bf:core:ready', { mobile: BF.isMobile() }); });
})();
